import React, { useEffect, useMemo, useState } from "react";
import {
  calculateThresholdProgress,
  formatRemainingPrice,
  getTitleFromOffer,
  isThresholdCampaignValid,
} from "./ThresholdMarketingProgress.service";
import { Box, Typography, useTheme } from "@mui/material";
import { useAppSelector } from "../../redux/hook";
import {
  MarketingCampaignType,
  Offer,
  ThresholdMarketingCampaignConfig,
} from "../../1_types/genericTypes";
import { ProgressComponents } from "@dishopsaas/design-system";
import { getColors, getPromotion } from "../../utils";
import { DISHOP_GREEN } from '../../constants';
export type ThresholdMarkertingProgressProps = {
  offer: Offer;
};

export default function ThresholdMarketingProgress({
  offer,
}: ThresholdMarkertingProgressProps) {
  const theme = useTheme();

  const { isMobile } = useAppSelector((state) => state.componentReducer);
  const { totalProductPriceWithPromo, orderType } = useAppSelector((state) => state.pendingOrderReducer);
  const { secondaryColor } = useAppSelector(
    (state) => state.configurationReducer
  );
  const [errorOccured, setErrorOccured] = useState(false);
  const config = offer.config as ThresholdMarketingCampaignConfig;

  const isValid = isThresholdCampaignValid(offer, orderType);
  if (!isValid) return null;

  const currentAmountForThreshold = useMemo(() => totalProductPriceWithPromo ?? 0, [totalProductPriceWithPromo]);

  const isComplete = currentAmountForThreshold >= config.threshold;
  const progressValue = calculateThresholdProgress(
    currentAmountForThreshold,
    config.threshold,
  )
  useEffect(() => {
    if (isComplete) setErrorOccured(false);
  }, [isComplete]);

  const progressLabel = useMemo(() => {
    const remainingAmount = config.threshold - currentAmountForThreshold;

    if (isComplete || remainingAmount <= 0) {
      return 'Promotion débloquée !';
    }

    const formattedRemaining = formatRemainingPrice(remainingAmount);
    return `Plus que ${formattedRemaining} pour débloquer ta promo !`;
  }, [isComplete, currentAmountForThreshold, config.threshold]);

  return !isValid || errorOccured ? (
    <></>
  ) : (
    <Box
      sx={{
        position: "fixed",
        bottom: 32,
        zIndex: 1,
        color: getColors().text,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: 3,
        p: 2,
        border: getColors().border,
        ...(isMobile
          ? {
            left: "50%",
            transform: "translateX(-50%)",
            width: "calc(100% - 32px)",
          }
          : {
            right: 32,
            width: { xs: 300, sm: 320 },
            maxWidth: 400,
          }),
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          component="img"
          src="/assets/thresholdMarketing.png"
          alt="threshold marketing"
          sx={{ height: 26, width: 26 }}
        />

        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color={getColors().text}
          >
            {getTitleFromOffer(offer)}
          </Typography>

          {offer.type ===
            MarketingCampaignType.PRODUCT_DISCOUNT_AFTER_THRESHOLD && (
              <Typography variant="caption" color={getColors().text}>
                *Sur une sélection de produits
              </Typography>
            )}
        </Box>
      </Box>

      <ProgressComponents.LinearProgressWithLabel
        value={progressValue}
        label={progressLabel}
        progressColor={isComplete
          ? DISHOP_GREEN
          : secondaryColor ?? "#E6BD25"
        }
      />
    </Box>
  );
}
